import { Build, Env } from '@stencil/core';
import {
  Store,
  applyMiddleware,
  compose,
  createStore
} from 'redux';
import freeze from 'redux-freeze';
import logger from 'redux-logger';
import { persistStore } from 'redux-persist';
import thunk, { ThunkDispatch } from 'redux-thunk';

import { Actions } from './actions';
import { analyticsMiddleware } from './analytics.middleware';
import { appMiddleware } from './app.middleware';
import { sentryMiddleware } from './sentry.middleware';
import { appSwitchEnvironment, init } from './app/app.actions';
import { AppEnvironment } from './app/app.reducer';
import rootReducer, { ApplicationState } from './reducers';
import { registerExpressionNamespaces } from '../services/expressions/registerExpressionNamespaces';

registerExpressionNamespaces();

const reduxMiddleware = [
  thunk,
  sentryMiddleware,
  analyticsMiddleware,
  appMiddleware,
  freeze,
];

if (Build.isDev && !Build.isTesting) {
  reduxMiddleware.push(logger as any);
}

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

// Define this type to essentially appease the typescript lint process
export type appStore = Store<ApplicationState> & { dispatch: ThunkDispatch<ApplicationState, undefined, Actions> };

export const initialStore: appStore = createStore(rootReducer, undefined, composeEnhancers(
  applyMiddleware(...reduxMiddleware)
));

const parseAppEnvironment = (value: string | null | undefined): AppEnvironment | null => {
  if (value === 'local' || value === 'dev' || value === 'prod') {
    return value;
  }
  return null;
};

const resolveBootstrapEnvironment = (): AppEnvironment | null => {
  // Query wins (manual / one-off); then Stencil Env from COREO_DEFAULT_ENV at serve/build time.
  const fromQuery = typeof window !== 'undefined'
    ? parseAppEnvironment(new URLSearchParams(window.location.search).get('env'))
    : null;
  if (fromQuery) {
    return fromQuery;
  }
  return parseAppEnvironment(Env.defaultEnvironment);
};

export const persistor = () => persistStore(initialStore, {}, async () => {
  const env = resolveBootstrapEnvironment();
  if (env) {
    await initialStore.dispatch(appSwitchEnvironment(env));
  }
  // tslint:disable-next-line: no-unbound-method
  await init(initialStore.dispatch, initialStore.getState);
});
